/**
 * 운동 지표의 표시 이름과 설명. 화면 문구의 SoT 다.
 *
 * 이름이 JSX 안에 흩어져 있으면 같은 값을 자리마다 다른 이름으로 부른다.
 * `diagnosticsTerms` 가 같은 방식을 쓴다.
 *
 * **단위는 적지 않는다.** 이유는 다음과 같다.
 * - BE 는 틀린 단위를 카탈로그 전체 아티팩트에 고정하지 않으려고 단위를 비워 보낸다.
 *   `unit` 은 항상 null 이다.
 * - FE 에도 채널 단위를 선언하는 계약이 없다. 데이터셋 구조에 unit 필드가 없다.
 * - 여기서 `m/s` 를 표시하면 BE 가 피한 오류를 화면이 대신 만든다.
 * 그래서 값은 원본 채널 단위 그대로 두고, 캡션이 이 사실을 알린다.
 *
 * 이름은 `한글(영어)` 로 함께 적는다. 상류 지표 key 와 대조할 수 있어야 하기 때문이다.
 * 한국어만 두면 두 2차 지표를 같은 것으로 읽는다.
 * 한국어는 **상류 표기를 그대로 따른다**(hatchery-data `83b9752`).
 * - `speed` 는 방향 없는 스칼라라서 `속력`이다.
 * - `acceleration` 은 `|dv/dt|` 라서 `가속도 크기`다.
 * 화면이 상류 문서·CLI 와 다른 이름을 쓰면 같은 값을 두 이름으로 부르게 된다.
 */

export type MotionTerm = {
  label: string;
  tip: string;
};

const METRIC_TERMS: Record<string, MotionTerm> = {
  speed: {
    label: "속력(speed)",
    tip: "기록 시각과 끝단 위치 xyz로 만든 곡선을 미분한 속도 벡터의 크기입니다. 방향이 없는 스칼라이며, pose의 회전 성분은 포함하지 않습니다.",
  },
  speed_change_rate: {
    label: "속력 증감률(speed change rate)",
    tip: "속력이 커지고 작아지는 정도입니다. 부호가 있어 감속하는 구간은 음수입니다. 가속도 크기와 다른 값입니다.",
  },
  acceleration: {
    label: "가속도 크기(acceleration)",
    tip: "속도 벡터가 변한 크기입니다. 방향만 꺾여도 값이 생기므로 속력이 일정해도 0 이 아닙니다.",
  },
};

const INDEXED_TERMS: Record<string, MotionTerm> = {
  torque: {
    label: "토크(torque)",
    tip: "미분하지 않은 원본 토크 값입니다. 성분마다 따로 옵니다.",
  },
  value: {
    label: "값(raw value)",
    tip: "미분하지 않은 원본 값입니다. 그리퍼처럼 상태를 나르는 채널이 여기에 듭니다.",
  },
};

/** 지표 key 의 표시 이름과 설명. 성분이 있는 지표(`torque.0`)는 이름에 성분 번호를 붙인다. */
export function motionMetricTerm(key: string): MotionTerm {
  const known = METRIC_TERMS[key];
  if (known !== undefined) {
    return known;
  }
  const dot = key.indexOf(".");
  if (dot > 0) {
    const base = INDEXED_TERMS[key.slice(0, dot)];
    if (base !== undefined) {
      return { label: `${base.label} ${key.slice(dot + 1)}`, tip: base.tip };
    }
  }
  // 상류가 지표를 추가해도 화면은 key 를 그대로 보여 준다. 이름을 지어내면 뜻이 틀어진다.
  return { label: key, tip: "상류가 낸 지표입니다." };
}

// 채널 요약을 생략한 사유(상류 `unavailable`)를 문구로 바꾼다. 모르는 사유는 key 를 그대로
// 둔다. 그래서 상류가 사유를 추가해도 사유가 빈칸으로 사라지지 않는다.
const UNAVAILABLE_TEXT: Record<string, string> = {
  no_rows: "기록된 행이 없어 계산하지 않았습니다.",
  invalid_timeline: "시간축이 깨져 계산하지 않았습니다.",
  non_numeric_values: "값이 숫자가 아니어서 계산하지 않았습니다.",
  unsupported_pose_dimension: "지원하지 않는 pose 차원이라 계산하지 않았습니다.",
};

/** 채널 요약을 생략한 사유의 문구. */
export function motionUnavailableText(reason: string): string {
  return UNAVAILABLE_TEXT[reason] ?? reason;
}

/** 통계 축의 표시 이름. */
export function motionStatLabel(stat: "mean" | "p95" | "rms" | "max"): string {
  switch (stat) {
    case "mean":
      return "평균";
    case "p95":
      // 뜻은 한국어로 적고, 상류 문서·`hd-inspect` 와 대조할 key 도 함께 둔다.
      return "상위 5%(p95)";
    case "rms":
      return "RMS";
    case "max":
      return "최대";
  }
}

/** 통계 축의 설명. 사용자가 시간 가중 값과 표본 분위수를 혼동하지 않게 한다. */
export function motionStatTip(stat: "mean" | "p95" | "rms" | "max"): string {
  switch (stat) {
    case "mean":
      return "구간 안의 시간 가중 평균입니다.";
    case "p95":
      return "구간 안 분석용 표본의 95백분위수입니다. 시간 가중 분위수는 아닙니다.";
    case "rms":
      return "구간 안의 시간 가중 제곱평균제곱근입니다.";
    case "max":
      return "구간 안의 최댓값과 그 값이 나온 시각입니다.";
  }
}
